// frontend/src/lib/analysisCredits.js  (BL-040 — Trial Entitlements & Analysis Credits)
//
// ADVISORY trial-credit helpers for the UX layer (remaining-credits indicator,
// low/exhaustion messaging, upgrade prompts). The BACKEND is the security
// boundary — these only drive display. Pure ESM (no React) so it can be unit
// tested directly and reused by SubscriptionContext + PaywallBanner.
//
// "Metered" means the resolved BL-041 analysis_volume limit is finite — i.e.
// the user is trialing (paid tiers resolve to null = unmetered; free = 0 but
// is blocked earlier at the capability gate). Usage is the count of the user's
// rows in the analysis_credit_events ledger (readable via RLS: SELECT own rows).

export const LOW_CREDITS_THRESHOLD = 2; // show the "running low" nudge at ≤2 left
export const LEDGER_TABLE = "analysis_credit_events";
export const CREDITS_EXHAUSTED_CODE = "analysis_credits_exhausted";

/**
 * Derive the advisory credit state from the resolved entitlement `limits`
 * (from getEntitlementContext) and the consumed `used` count.
 *   → { metered, limit, used, remaining, exhausted, lowCredits }
 * Unmetered (paid) tiers return metered:false and null limit/remaining.
 */
export function deriveCreditState(limits, used = 0) {
  const limit = limits ? limits.analysis_volume : undefined;
  const metered = limit !== null && limit !== undefined;

  if (!metered) {
    return { metered: false, limit: null, used: 0, remaining: null, exhausted: false, lowCredits: false };
  }

  const consumed = Number(used) || 0;
  const remaining = Math.max(0, limit - consumed);
  return {
    metered: true,
    limit,
    used: consumed,
    remaining,
    exhausted: remaining <= 0,
    lowCredits: remaining > 0 && remaining <= LOW_CREDITS_THRESHOLD,
  };
}

/**
 * True when an analyze-property response is the BL-040 credit-exhaustion
 * response (so the analyzer can show exhaustion + upgrade, not a generic error).
 */
export function isCreditsExhaustedResponse(data) {
  return !!data && data.code === CREDITS_EXHAUSTED_CODE;
}

/**
 * Read the user's consumed trial-credit count from the ledger (RLS: own rows).
 * Returns 0 on any error (advisory only — never blocks the UI; the backend is
 * the real boundary). `supabase` is the browser client; `userId` the auth uid.
 */
export async function fetchTrialCreditUsage(supabase, userId) {
  if (!supabase || !userId) return 0;
  try {
    const { count, error } = await supabase
      .from(LEDGER_TABLE)
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId);
    if (error) return 0;
    return count || 0;
  } catch {
    return 0;
  }
}
